import { useCallback, useRef } from 'react';
import first from 'lodash/first';
import { useMutation } from '@apollo/react-hooks';
import type { ApolloError } from 'apollo-client';
// eslint-disable-next-line jira/restricted/date-fns
import dateFormat from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import {
	isAbortError,
	AbortError,
	DEFAULT_ABORT_ERROR_MESSAGE,
} from '@atlassian/jira-business-error-handling/src/utils/abort-error/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import FetchError, { ValidationError } from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { MutationError } from '@atlassian/jira-mutation-error/src/index.tsx';
import { DateOnly } from '@atlassian/jira-platform-utils-date-only/src/index.tsx';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useStartDateField } from '@atlassian/jira-router-resources-business-configuration/src/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type {
	ScheduleTimelineJwmItem,
	ScheduleTimelineJwmItemVariables,
} from './__generated_apollo__/ScheduleTimelineJwmItem';
import { SCHEDULE_TIMELINE_ITEM_MUTATION } from './gql.tsx';

export interface ExecutionResult<T> {
	data?: T;
	reason?: string;
}

type OnScheduleItemFunction = (item: {
	issueId: string;
	startDate?: number;
	dueDate?: number;
	isDragOperation: boolean;
}) => Promise<boolean | undefined>;

const DATE_FORMAT = 'dd/MMM/yyyy';
const QUERY_NAME = 'scheduleTimelineJwmItem';

const mapDateToValidDateVariable = (date: number | string) => {
	const mappedDate = DateOnly.fromDateAccordingToUTC(new Date(date)).toISOString();
	return dateFormat(parseISO(mappedDate), DATE_FORMAT);
};

export class NotFoundError extends ValidationError {
	constructor() {
		super('Issue not found');
	}
}

export const useScheduleItem = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const cloudId = useCloudId();
	const { data: startDateField } = useStartDateField();
	const abortController = useRef<AbortController>(new AbortController());

	const doFireErrorAnalytics = (error: ApolloError | MutationError[]) => {
		fireErrorAnalytics({
			meta: {
				id: QUERY_NAME,
				packageName: 'jiraBusinessTimeline',
				teamName: 'jira-yurree',
			},
			error: Array.isArray(error) ? error[0] : error,
			attributes: { message: 'Failed to schedule item' },
			sendToPrivacyUnsafeSplunk: true,
		});
	};

	const [scheduleItem] = useMutation<ScheduleTimelineJwmItem, ScheduleTimelineJwmItemVariables>(
		SCHEDULE_TIMELINE_ITEM_MUTATION,
		{
			onError: (apolloError) => {
				if (isAbortError(apolloError.networkError)) {
					throw apolloError.networkError;
				}

				// we need to filter out 4xx errors to reduce noise
				const not4xxErrors = apolloError.graphQLErrors?.filter(
					(error) => !error.extensions?.statusCode || error.extensions.statusCode >= 500,
				);

				if (not4xxErrors?.length > 0) {
					doFireErrorAnalytics(apolloError);
				}

				const firstError = first(apolloError?.graphQLErrors);

				throw new FetchError(
					firstError?.extensions?.statusCode || 500,
					firstError?.message || 'Timeline schedule item mutation returned an error',
				);
			},
			onCompleted: (onCompletedData) => {
				fireOperationalAnalytics(createAnalyticsEvent({}), `${QUERY_NAME} succeeded`, {});
				if (onCompletedData.scheduleTimelineJwmItem?.errors?.length) {
					doFireErrorAnalytics(
						onCompletedData.scheduleTimelineJwmItem.errors.map(
							(e) => new MutationError({ message: e.message }),
						),
					);
				} else if (onCompletedData?.scheduleTimelineJwmItem?.success === false) {
					doFireErrorAnalytics([
						new MutationError({
							message: 'Success mutation value as false without error',
						}),
					]);
				}
			},
		},
	);

	const onScheduleItem: OnScheduleItemFunction = useCallback(
		({ issueId, startDate, dueDate, isDragOperation }) => {
			abortController.current.abort(new AbortError(DEFAULT_ABORT_ERROR_MESSAGE));
			abortController.current = new AbortController();

			const variables = {
				input: {
					issueId: `ari:cloud:jira:${cloudId}:issue/${issueId}`,
					operation: isDragOperation ? 'DRAG' : 'SET',
					...(startDate != null && startDateField?.id
						? {
								startDate: {
									fieldId: startDateField.id,
									date: {
										formattedDate: mapDateToValidDateVariable(startDate),
									},
								},
							}
						: {}),
					...(dueDate != null
						? {
								dueDate: {
									fieldId: 'duedate',
									date: {
										formattedDate: mapDateToValidDateVariable(dueDate),
									},
								},
							}
						: {}),
				},
			};

			return scheduleItem({
				// @ts-expect-error - TS2305 -  Type 'string' is not assignable to type 'JWMScheduleTimelineItemOperation'.
				variables,
				context: {
					fetchOptions: {
						signal: abortController.current.signal,
					},
				},
			}).then((result) => {
				const error = first(result?.data?.scheduleTimelineJwmItem?.errors);

				if (error || result?.data?.scheduleTimelineJwmItem?.success !== true) {
					throw new Error(
						error?.message ||
							'Timeline schedule item mutation result data included an error or success was not true',
					);
				}

				return result?.data?.scheduleTimelineJwmItem?.success;
			});
		},
		[cloudId, startDateField, scheduleItem],
	);

	return {
		onScheduleItem,
	};
};
